import {
  CalendarToday,
  Cancel,
  CreditCard,
  East,
  ElectricBolt,
  GroupAddOutlined,
  History as HistoryIcon,
  Launch,
  LocationOnOutlined,
  ReportGmailerrorred,
  ScheduleOutlined,
  TaskAltOutlined,
} from "@mui/icons-material";
import { Box, Button, Card, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, LinearProgress, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { useDispatch, useSelector } from "react-redux";
import { assets } from "../assets";
import { useGlobalSnackbar } from "../context/SnackbarContext";
import { useWatchBaseNotification } from "../notifications/hooks/useWatchBaseNotification";
import { ENotificationType } from "../notifications/interfaces";
import { EBenefitTypes } from "../permissions/benefits/enums/benefits.enums";
import { initialBenefitsFetch } from "../permissions/benefits/redux/benefits.init";
import { setPlanSelected } from "../redux/features/planStateSlice";
import { updateUser } from "../redux/features/userStateSlice";
import { ESubscriptionStatus, cancelMySubscriptionInfo } from "../utils";
import { LoaderComponent } from "./Loader";
import { BaseModalComponent } from "./common/BaseModalComponent";
import { SecondarySubscriptionInfo, SubscriptionSection, getLatestBillingPeriodInfo } from "./layout/ParentsSubscriptionInfo";
import { formatCompactBillingMethod } from "./subscriptions/actions/get-billing-vaults.actions";
import { UpdateFixedPaymentMethod } from "./UpdateFixedPaymentMethod";
import { useCompanyBillingVaultsQuery } from "./subscriptions/hooks/useCompanyBillingVaultsQuery";
import { EQueryKeys, useMySubscriptionInfoSync } from "./subscriptions/hooks/useMySubscriptionInfo.query";
import { useRetrySubscriptionPayment } from "./subscriptions/hooks/useRetrySubscription.mutation";
import { SubscriptionPaymentHistoryModal } from "./superadmin/billing/components/SubscriptionPaymentHistoryModal";
import { alphaVar, colors } from "./ui/colors";

const PAYPAL_ACCOUNT_URL = "https://www.paypal.com/myaccount/summary";

export const PlanInfo = ({ setCancel = () => {}, onUpgrade = () => {}, ...rest }) => {
  const userState = useSelector((state) => state.userState);
  const subscriptionInfo = userState.subscriptionInfo;
  const subscription = subscriptionInfo?.subscription;
  const [loading, setLoading] = React.useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState(null);
  const [cardDialogOpen, setCardDialogOpen] = useState(false);
  const coverageBenefits = userState?.benefits?.find((item) => item.type === EBenefitTypes.AREASCOVERAGES);
  const connectionBenefits = userState?.benefits?.find((item) => item.type === EBenefitTypes.SEATS);
  const subscriptionDetails = userState?.subscriptionDetails;
  const token = sessionStorage.getItem("token");

  const { showBoundary } = useErrorBoundary();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { showSnackbar } = useGlobalSnackbar();
  useMySubscriptionInfoSync();

  const isFreeTrialPlan = subscription?.paypalId === "free_trial" || subscription?.paypalSubscriptionId === "free_trial" || subscription?.plan?.product?.name?.toLowerCase() === "free";
  const { data: billingVaults = [] } = useCompanyBillingVaultsQuery(Boolean(subscription?.id));
  const mainBillingVault = billingVaults.find((vault) => vault.subscriptionId === subscription?.id) ?? null;
  const billingReference = subscription?.paypalId || subscription?.paypalSubscriptionId || "";
  const isPaypalBilling =
    mainBillingVault?.provider === "paypal" || (!mainBillingVault && (billingReference.startsWith("I-") || billingReference.startsWith("B-") || Boolean(subscriptionDetails)));
  const billingMethodLabel =
    formatCompactBillingMethod(mainBillingVault) || (isFreeTrialPlan ? "Free" : isPaypalBilling ? "PayPal" : billingReference.startsWith("pm_") ? "Stripe" : "Card on file");

  const { mutate: retryPayment, isPending: isRetryingPayment } = useRetrySubscriptionPayment({
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({
        queryKey: [EQueryKeys.MY_SUBSCRIPTION_INFO],
      });
      dispatch(initialBenefitsFetch());

      if (response?.data?.recovered) {
        showSnackbar(response.data.message || "Subscription payment retry processed successfully and subscription is active.", "success");
      } else {
        showSnackbar(response?.data?.message || "Payment retry was attempted, but the subscription is not active yet.", "error");
      }
    },
    onError: (error) => {
      console.error(error);
      showSnackbar(error?.message || "An error occurred while retrying the payment.", "error");
    },
  });
  const isSuspended = subscription?.status === ESubscriptionStatus.SUSPENDED || subscription?.status === ESubscriptionStatus.FAILED;
  const isCancelled = subscription?.status === ESubscriptionStatus.CANCELLED;
  const isFailed = subscription?.status === ESubscriptionStatus.FAILED;

  useWatchBaseNotification({
    notificationTypes: [
      ENotificationType.PlanSubscriptionFailed,
      ENotificationType.PlanSubscriptionRestored,
      ENotificationType.PlanSubscriptionSuspended,
      ENotificationType.PlanSubscriptionCancelled,
    ],
    dissableSnackbarAlert: true,
    onNotificationArrived: () => {
      void queryClient.invalidateQueries({
        queryKey: [EQueryKeys.MY_SUBSCRIPTION_INFO],
      });
      dispatch(initialBenefitsFetch());
    },
  });

  const otherSubscriptions = subscriptionInfo?.subscriptions ?? [];

  const validOtherSubscriptions = otherSubscriptions.filter((item) => {
    const currentSubscription = item?.subscription;
    const currentId = currentSubscription?.id;

    if (!currentId) return false;
    if (currentId === subscription?.id) return false;
    if (currentSubscription?.paypalId === "free_trial") return false;

    return true;
  });

  const hasActiveOtherSubscriptions = validOtherSubscriptions.some((item) => {
    const status = item?.subscription?.status?.toUpperCase();

    return status !== "CANCELLED";
  });

  const hasCancelledOtherSubscriptions = validOtherSubscriptions.some((item) => {
    const status = item?.subscription?.status?.toUpperCase();

    return status === "CANCELLED";
  });

  const shouldShowUpgradeInvitation = isFreeTrialPlan && !hasActiveOtherSubscriptions && !hasCancelledOtherSubscriptions;

  const formatDate = (date) => {
    if (!date || isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  };

  useEffect(() => {}, [subscription]);

  const handleCancelMySubscription = async () => {
    try {
      setLoading(true);
      await cancelMySubscriptionInfo(token, subscription?.id);

      dispatch(
        updateUser({
          subscription: null,
          benefits: [],
        }),
      );

      setCancel(true);
    } catch (error) {
      showBoundary(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeMySubscription = async () => {
    try {
      setLoading(true);

      dispatch(
        updateUser({
          subscription: null,
          benefits: [],
        }),
      );
      dispatch(
        setPlanSelected({
          upgradeFreeTrial: !isCancelled,
        }),
      );

      onUpgrade();
      setCancel(true);
    } catch (error) {
      showBoundary(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRetrySubscriptionPayment = () => {
    if (!subscription?.id || isRetryingPayment) return;

    retryPayment(subscription.id);
  };

  const handleOpenDialog = (type = "cancel") => {
    setDialogType(type);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleAction = () => {
    handleCancelMySubscription();
    setOpenDialog(false);
  };

  const coveragePercentage = (coverageBenefits?.CompanyBenefits?.usageCount / coverageBenefits?.config?.limitedUsage) * 100 || 0;
  const connectionPercentage = (connectionBenefits?.CompanyBenefits?.usageCount / connectionBenefits?.config?.limitedUsage) * 100 || 0;

  const getSubscriptionChipConfig = (status) => {
    switch (status) {
      case ESubscriptionStatus.COMPLETED:
      case ESubscriptionStatus.ACTIVE:
        return {
          label: "Active",
          bgColor: colors.orange.base,
        };

      case ESubscriptionStatus.SUSPENDED:
        return {
          label: "Suspended",
          bgColor: colors.danger[100],
        };

      case ESubscriptionStatus.FAILED:
        return {
          label: "Payment Failed",
          bgColor: colors.danger.base,
        };

      case ESubscriptionStatus.CANCELLED:
        return {
          label: "Cancelled",
          bgColor: colors.danger.base,
        };

      case ESubscriptionStatus.INACTIVE:
      default:
        return {
          label: "Inactive",
          bgColor: colors.gray[400],
        };
    }
  };

  const subscriptionMeta = (() => {
    const meta = subscription?.meta;
    if (!meta) return {};
    if (typeof meta === "string") {
      try {
        return JSON.parse(meta);
      } catch {
        return {};
      }
    }
    return meta;
  })();
  const pendingPlanName = subscription?.nextPlan?.name;
  const hasPendingPlan = Boolean(subscriptionMeta.nextPlanId);
  const isStripeBillingPlan = Boolean(subscriptionMeta.stripeBillingScheduleId);
  const activeCompanyId = userState.activeCompanyId || userState.company?.id;
  const pendingPlanExpiry = subscription?.expDate ? formatDate(new Date(subscription.expDate)) : null;
  const subscriptionChip = getSubscriptionChipConfig(subscription?.status);
  const mainBillingInfo = getLatestBillingPeriodInfo(subscription);
  const subscriptionStatusColor =
    isFailed || isCancelled ? colors.danger.base : subscription?.status === ESubscriptionStatus.SUSPENDED ? colors.warning.base : colors.success.base;
  const recurringStatuses = [ESubscriptionStatus.COMPLETED, ESubscriptionStatus.ACTIVE, ESubscriptionStatus.SUSPENDED, ESubscriptionStatus.FAILED];
  const nextDateLabel = isFailed
    ? "Next Retry"
    : subscription?.status === ESubscriptionStatus.COMPLETED || subscription?.status === ESubscriptionStatus.ACTIVE
      ? "Next Billing"
      : "Expires On";
  const nextDateValue =
    isCancelled && !isFreeTrialPlan
      ? formatDate(new Date(subscription?.expDate))
      : isFailed
        ? subscriptionDetails?.nextRetryPayment
          ? formatDate(new Date(subscriptionDetails?.nextRetryPayment))
          : "N/A"
        : !isFreeTrialPlan && subscriptionDetails?.nextPayment
          ? formatDate(new Date(subscriptionDetails?.nextPayment))
          : "N/A";

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        mb: { sm: 0, xs: 3 },
        flexDirection: "column",
        ...rest.sx,
      }}
      {...rest}
    >
      <Dialog open={cardDialogOpen} onClose={() => setCardDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Update billing card</DialogTitle>
        <DialogContent>
          {cardDialogOpen && activeCompanyId && subscription?.id && (
            <UpdateFixedPaymentMethod
              companyId={Number(activeCompanyId)}
              subscriptionId={subscription.id}
              onSuccess={() => {
                setCardDialogOpen(false);
                showSnackbar("Billing card updated.", "success");
                void queryClient.invalidateQueries({ queryKey: ["subscriptions", "billing-vaults"] });
              }}
            />
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setCardDialogOpen(false)}>Close</Button></DialogActions>
      </Dialog>
      <Box mb={3} display={"flex"} width="100%" gap={3} justifyContent={"space-between"} alignItems="flex-start" sx={{ flexDirection: "column" }}>
        {subscription ? (
          /* Current Plan Card */
          <Card
            sx={{
              width: "100%",
              borderRadius: "20px",
              overflow: "hidden",
              transition: "all 0.3s ease",
              backgroundColor: "var(--fx-surface-base, #FFFFFF)",
              border: "1px solid var(--fx-surface-border, #E2E8F0)",
              boxShadow: "0 2px 12px rgba(0,0,0,0.02)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
              {/* Header Row */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2, mb: 0.5 }}>
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: "0.9rem",
                      color: "#FF6B00",
                      mb: 0.5,
                    }}
                  >
                    Current Plan
                  </Typography>

                  <Typography
                    sx={{
                      fontWeight: 800,
                      letterSpacing: "-0.5px",
                      fontSize: { xs: "1.75rem", sm: "2.25rem" },
                      color: "var(--fx-surface-text, #0F172A)",
                      lineHeight: 1.1,
                    }}
                  >
                    {subscription?.plan?.product?.name || "Business"}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Chip
                    label={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: subscriptionStatusColor }} />
                        <span>{subscriptionChip.label}</span>
                      </Box>
                    }
                    sx={{
                      bgcolor:
                        subscription?.status === ESubscriptionStatus.ACTIVE || subscription?.status === ESubscriptionStatus.COMPLETED
                          ? "#E6F4EA"
                          : "#FEF2F2",
                      color: subscriptionStatusColor,
                      fontSize: "13px",
                      fontWeight: 700,
                      borderRadius: "16px",
                      height: 28,
                      px: 1,
                      border: "none",
                    }}
                  />
                  {isFailed || subscription?.status === ESubscriptionStatus.SUSPENDED ? (
                    <ReportGmailerrorred sx={{ color: colors.danger.base, fontSize: "2.2rem" }} />
                  ) : isCancelled ? (
                    <Cancel sx={{ color: colors.danger.base, fontSize: "2.2rem" }} />
                  ) : (
                    <TaskAltOutlined sx={{ color: "#FF6B00", fontSize: "2.2rem" }} />
                  )}
                </Box>
              </Box>

              {/* Subtitle Description */}
              <Typography sx={{ color: "var(--fx-surface-textsecondary, #64748B)", fontSize: "0.925rem", fontWeight: 500, mt: 0.5, mb: hasPendingPlan ? 1.25 : 3 }}>
                Everything you need to grow and manage your business.
              </Typography>

              {hasPendingPlan && (
                <Chip
                  icon={<ScheduleOutlined sx={{ fontSize: "16px !important", color: "#C2410C !important" }} />}
                  label={
                    pendingPlanExpiry && pendingPlanExpiry !== "N/A"
                      ? `Pending plan active: ${pendingPlanName || "selected plan"} · starts ${pendingPlanExpiry}`
                      : `Pending plan active: ${pendingPlanName || "selected plan"} · starts when this plan expires`
                  }
                  sx={{
                    mb: 3,
                    height: 30,
                    maxWidth: "100%",
                    bgcolor: "#FFF7ED",
                    color: "#9A3412",
                    fontWeight: 700,
                    fontSize: "0.78rem",
                    borderRadius: "16px",
                    border: "1px solid #FFEDD5",
                    "& .MuiChip-label": { px: 1.25 },
                  }}
                />
              )}

              {/* 3 Metric Columns Section */}
              <Grid container spacing={2} sx={{ mb: 3, alignItems: "center" }}>
                {/* Column 1: Billing Cycle */}
                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: "flex", gap: 1.75, alignItems: "center" }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "14px",
                        bgcolor: "var(--fx-colors-warning-50, #FFF7ED)",
                        color: "#FF6B00",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <ElectricBolt sx={{ fontSize: 22 }} />
                    </Box>
                    <Box>
                      <Typography sx={{ color: "var(--fx-surface-textsecondary, #64748B)", fontWeight: 600, fontSize: "12px", mb: 0.25 }}>
                        Billing Cycle
                      </Typography>
                      <Typography sx={{ fontWeight: 800, color: "var(--fx-surface-text, #0F172A)", fontSize: "15px" }}>
                        {!isFreeTrialPlan &&
                        (subscription?.status === ESubscriptionStatus.COMPLETED || subscription?.status === ESubscriptionStatus.ACTIVE)
                          ? "Monthly"
                          : "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Column 2: Last Billing */}
                <Grid item xs={12} sm={4} sx={{ borderLeft: { sm: "1px solid var(--fx-surface-border, #E2E8F0)" }, pl: { sm: 2.5 } }}>
                  <Box sx={{ display: "flex", gap: 1.75, alignItems: "center" }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "14px",
                        bgcolor: "var(--fx-colors-warning-50, #FFF7ED)",
                        color: "#FF6B00",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <CalendarToday sx={{ fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography sx={{ color: "var(--fx-surface-textsecondary, #64748B)", fontWeight: 600, fontSize: "12px", mb: 0.25 }}>
                        Last Billing
                      </Typography>
                      <Typography sx={{ fontWeight: 800, color: "var(--fx-surface-text, #0F172A)", fontSize: "15px" }}>
                        {subscriptionDetails?.lastPayment ? formatDate(new Date(subscriptionDetails.lastPayment)) : "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Column 3: Expires On / Next Billing */}
                <Grid item xs={12} sm={4} sx={{ borderLeft: { sm: "1px solid var(--fx-surface-border, #E2E8F0)" }, pl: { sm: 2.5 } }}>
                  <Box sx={{ display: "flex", gap: 1.75, alignItems: "center" }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "14px",
                        bgcolor: "var(--fx-colors-warning-50, #FFF7ED)",
                        color: "#FF6B00",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <CalendarToday sx={{ fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography sx={{ color: "var(--fx-surface-textsecondary, #64748B)", fontWeight: 600, fontSize: "12px", mb: 0.25 }}>
                        {subscription?.status === ESubscriptionStatus.COMPLETED || subscription?.status === ESubscriptionStatus.ACTIVE
                          ? "Next Billing"
                          : "Expires On"}
                      </Typography>
                      <Typography sx={{ fontWeight: 800, fontSize: "15px", color: "#FF6B00" }}>
                        {subscription?.status === ESubscriptionStatus.CANCELLED && !isFreeTrialPlan
                          ? formatDate(new Date(subscription?.expDate))
                          : !isFreeTrialPlan && subscriptionDetails?.nextPayment
                            ? formatDate(new Date(subscriptionDetails.nextPayment))
                            : "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                  gap: 2,
                  mb: 3,
                }}
              >
                <Box
                  sx={{
                    borderRadius: "14px",
                    p: 2,
                    bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
                    border: "1px solid var(--fx-surface-border, #E2E8F0)",
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.25, gap: 1.5 }}>
                    <Box sx={{ display: "flex", gap: 1.2, minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", color: colors.orange.base, flexShrink: 0 }}>
                        <LocationOnOutlined size={16} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 900, fontSize: "0.9rem", color: "var(--fx-surface-text, #0F172A)", lineHeight: 1.2 }}>
                          Coverage Areas
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.72rem",
                            lineHeight: 1.2,
                            color: "var(--fx-surface-textsecondary, #64748B)",
                          }}
                        >
                          Regional deployment limit
                        </Typography>
                      </Box>
                    </Box>

                    <Typography sx={{ fontWeight: 800, fontSize: "0.9rem", color: "var(--fx-surface-text, #0F172A)", flexShrink: 0 }}>
                      {coverageBenefits?.CompanyBenefits?.usageCount ?? 0}
                      <span style={{ color: "#D9D9D9", fontWeight: 500, marginLeft: "4px" }}>/ {coverageBenefits?.config?.limitedUsage ?? 0}</span>
                    </Typography>
                  </Box>

                  <LinearProgress
                    variant="determinate"
                    value={coveragePercentage}
                    sx={{
                      height: 6,
                      borderRadius: 10,
                      bgcolor: "var(--fx-surface-muted, #F1F5F9)",
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 10,
                        background: `linear-gradient(90deg, ${colors.orange.base} 0%, ${alphaVar(colors.orange.base, 0.7)} 100%)`,
                      },
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    borderRadius: "14px",
                    p: 2,
                    bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
                    border: "1px solid var(--fx-surface-border, #E2E8F0)",
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.25, gap: 1.5 }}>
                    <Box sx={{ display: "flex", gap: 1.2, minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", color: colors.orange.base, flexShrink: 0 }}>
                        <GroupAddOutlined size={16} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 900, fontSize: "0.9rem", color: "var(--fx-surface-text, #0F172A)", lineHeight: 1.2 }}>
                          Seats Used
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.72rem",
                            lineHeight: 1.2,
                            color: "var(--fx-surface-textsecondary, #64748B)",
                          }}
                        >
                          Collaborators & team access
                        </Typography>
                      </Box>
                    </Box>

                    <Typography sx={{ fontWeight: 800, fontSize: "0.9rem", color: "var(--fx-surface-text, #0F172A)", flexShrink: 0 }}>
                      {connectionBenefits?.CompanyBenefits?.usageCount ?? 0}
                      <span style={{ color: "#D9D9D9", fontWeight: 500, marginLeft: "4px" }}>/ {connectionBenefits?.config?.limitedUsage ?? 0}</span>
                    </Typography>
                  </Box>

                  <LinearProgress
                    variant="determinate"
                    value={connectionPercentage}
                    sx={{
                      height: 6,
                      borderRadius: 10,
                      bgcolor: "var(--fx-surface-muted, #F1F5F9)",
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 10,
                        background: `linear-gradient(90deg, ${colors.orange.base} 0%, ${alphaVar(colors.orange.base, 0.7)} 100%)`,
                      },
                    }}
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  mb: 3,
                  border: "1px solid var(--fx-surface-border, #E2E8F0)",
                  borderRadius: "14px",
                  overflow: "hidden",
                  bgcolor: "var(--fx-surface-base, #FFFFFF)",
                }}
              >
                <SubscriptionSection title="Your Benefits" subscriptionItem={subscription} isParent hidePlanHeader />
              </Box>

              <Box
                sx={{
                  mb: 3,
                  p: { xs: 2, sm: 2.5 },
                  border: "1px solid var(--fx-surface-border, #E2E8F0)",
                  borderRadius: "14px",
                  bgcolor: "var(--fx-surface-base, #FFFFFF)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", gap: 1, color: "#FF6B00", alignItems: "center", minWidth: 120 }}>
                    <CreditCard size={20} />
                    <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: "var(--fx-surface-text, #0F172A)" }}>Billing</Typography>
                  </Box>

                  <Grid container spacing={2} sx={{ flex: 1, minWidth: { xs: "100%", md: 0 } }}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="overline" sx={{ color: "var(--fx-surface-text, #0F172A)", fontWeight: 800, fontSize: "0.65rem", display: "block", mb: 0.75 }}>
                        METHOD
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {isPaypalBilling && <img src={assets.images.paypalLogo} width={16} height={16} alt="PayPal" />}
                        <Typography sx={{ fontWeight: 600, fontSize: "0.78rem", lineHeight: 1, whiteSpace: "nowrap", color: "var(--fx-surface-textsecondary, #64748B)" }}>
                          {billingMethodLabel}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={3}>
                      <Typography variant="overline" sx={{ color: "var(--fx-surface-text, #0F172A)", fontWeight: 800, fontSize: "0.65rem", display: "block", mb: 0.75 }}>
                        STATUS
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.78rem",
                          lineHeight: 1,
                          whiteSpace: "nowrap",
                          color: subscription?.status === ESubscriptionStatus.CANCELLED ? colors.danger.base : colors.success.base,
                        }}
                      >
                        {subscription?.status?.charAt(0)?.toUpperCase() + subscription?.status?.slice(1)}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={5}>
                      <Typography variant="overline" sx={{ color: "var(--fx-surface-text, #0F172A)", fontWeight: 800, fontSize: "0.65rem", display: "block", mb: 0.75 }}>
                        BILLING CONTACT
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.78rem",
                          lineHeight: 1.2,
                          color: "var(--fx-surface-textsecondary, #64748B)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {subscriptionDetails?.subscriberEmail || "N/A"}
                      </Typography>
                    </Grid>
                  </Grid>

                  {isStripeBillingPlan && activeCompanyId && !isCancelled && (
                    <Button size="small" onClick={() => setCardDialogOpen(true)} sx={{ textTransform: "none" }}>Update card</Button>
                  )}
                </Box>

                {isPaypalBilling && subscriptionDetails && (
                  <Button
                    href={PAYPAL_ACCOUNT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    endIcon={<Launch sx={{ fontSize: 14 }} />}
                    sx={{
                      color: colors.info.base,
                      fontWeight: 700,
                      width: 90,
                      lineHeight: 1,
                      textAlign: "right",
                      textTransform: "none",
                      fontSize: "0.65rem",
                      p: 0,
                      whiteSpace: "normal",
                      "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
                    }}
                  >
                    View Invoice History
                  </Button>
                )}
              </Box>

              {/* Divider Line */}
              <Divider sx={{ my: 2.5, borderColor: "var(--fx-surface-border, #E2E8F0)" }} />

              {/* Action Buttons Row */}
              <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                <Button
                  onClick={isSuspended ? handleRetrySubscriptionPayment : handleUpgradeMySubscription}
                  variant="contained"
                  disabled={isRetryingPayment}
                  endIcon={<East sx={{ fontSize: 18 }} />}
                  sx={{
                    bgcolor: isSuspended ? colors.danger.base : "#FF6B00",
                    color: "#FFFFFF",
                    fontWeight: 800,
                    fontSize: "14px",
                    textTransform: "none",
                    py: 1.2,
                    px: 3.5,
                    borderRadius: "12px",
                    boxShadow: "none",
                    height: 42,
                    "&:hover": {
                      bgcolor: isSuspended ? colors.danger[100] : "#E05E00",
                      boxShadow: "none",
                    },
                  }}
                >
                  {isSuspended ? (isRetryingPayment ? "Retrying Payment..." : "Retry Payment") : isCancelled ? "Subscribe Again" : "Upgrade Plan"}
                </Button>

                {Array.isArray(subscription?.billingPeriods) && subscription.billingPeriods.length > 0 && (
                  <BaseModalComponent
                    modalKey={`plan-info-payment-history-${subscription?.id || "default"}`}
                    modalLabel={`Payment History · ${subscription?.plan?.product?.name || "Subscription"}`}
                    defaultSizes={{ width: 820, height: 600 }}
                    elementTrigger={
                      <Button
                        variant="outlined"
                        startIcon={
                          <HistoryIcon
                            sx={{
                              fontSize: 18,
                              color: mainBillingInfo?.hasPendingPayment ? (mainBillingInfo.isFailed ? colors.danger.base : "#B45309") : "#334155",
                            }}
                          />
                        }
                        sx={{
                          color: mainBillingInfo?.hasPendingPayment ? (mainBillingInfo.isFailed ? colors.danger.base : "#B45309") : "#0F172A",
                          borderColor: mainBillingInfo?.hasPendingPayment ? (mainBillingInfo.isFailed ? colors.danger.base : "#F59E0B") : "#E2E8F0",
                          bgcolor: mainBillingInfo?.hasPendingPayment
                            ? mainBillingInfo.isFailed
                              ? alphaVar(colors.danger.base, 0.08)
                              : "#FEF3C7"
                            : "#FFFFFF",
                          fontWeight: 700,
                          fontSize: "14px",
                          textTransform: "none",
                          py: 1.2,
                          px: 2.5,
                          borderRadius: "12px",
                          height: 42,
                          boxShadow: mainBillingInfo?.hasPendingPayment
                            ? `0 0 0 2px ${mainBillingInfo.isFailed ? alphaVar(colors.danger.base, 0.2) : "rgba(245, 158, 11, 0.3)"}`
                            : "none",
                          "&:hover": {
                            borderColor: mainBillingInfo?.hasPendingPayment ? (mainBillingInfo.isFailed ? colors.danger.base : "#D97706") : "#CBD5E1",
                            bgcolor: mainBillingInfo?.hasPendingPayment
                              ? mainBillingInfo.isFailed
                                ? alphaVar(colors.danger.base, 0.16)
                                : "#FDE68A"
                              : "#F8FAFC",
                          },
                        }}
                      >
                        Billing History
                        <Box
                          component="span"
                          sx={{
                            ml: 1,
                            px: 1,
                            py: 0.2,
                            borderRadius: "10px",
                            bgcolor: mainBillingInfo?.hasPendingPayment
                              ? mainBillingInfo.isFailed
                                ? alphaVar(colors.danger.base, 0.16)
                                : "#F59E0B"
                              : "#F1F5F9",
                            color: mainBillingInfo?.hasPendingPayment ? "#FFFFFF" : "var(--fx-surface-textsecondary, #64748B)",
                            fontSize: "12px",
                            fontWeight: 800,
                          }}
                        >
                          {subscription.billingPeriods.length}
                        </Box>
                        {mainBillingInfo?.hasPendingPayment && (
                          <Box
                            component="span"
                            sx={{
                              ml: 1,
                              fontSize: "12px",
                              fontWeight: 800,
                            }}
                          >
                            · {mainBillingInfo.isFailed ? "Payment Failed" : "Action Needed"}
                          </Box>
                        )}
                      </Button>
                    }
                  >
                    <SubscriptionPaymentHistoryModal subscription={subscription} />
                  </BaseModalComponent>
                )}
              </Box>
            </Box>
          </Card>
        ) : (
          <Card
            sx={{
              width: "100%",
              borderRadius: 3,
              p: 3,
              backgroundColor: "#FFFFFF",
              boxShadow: "none",
              border: `1px solid ${alphaVar(colors.orange.base, 0.1)}`,
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 3 }}>
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: colors.orange.base, mb: 1 }}>Main Subscription Plan</Typography>
                <Typography sx={{ fontWeight: 800, letterSpacing: -0.5, fontSize: { xs: "1.5rem", sm: "2rem" }, color: "#1A1C1E", mb: 1 }}>
                  No Active Flownect Plan
                </Typography>
                <Typography sx={{ color: "#475569", fontSize: "0.9rem", maxWidth: 600 }}>
                  To unlock premium features, explore our available plans.
                </Typography>
              </Box>
              <Button
                onClick={handleUpgradeMySubscription}
                variant="contained"
                endIcon={<East />}
                sx={{
                  background: isSuspended ? colors.danger.base : colors.orange.base,
                  color: "white",
                  fontWeight: 700,
                  textTransform: "none",
                  py: 1.2,
                  px: 4,
                  borderRadius: 2,
                  boxShadow: "none",
                  "&:hover": { bgcolor: colors.orange[600], boxShadow: "none" },
                  borderColor: colors.orange.base,
                  height: 40,
                }}
              >
                Explore Plans
              </Button>
            </Box>
          </Card>
        )}

        <Box sx={{ width: "100%" }}>
          <SecondarySubscriptionInfo />
        </Box>
      </Box>

      {/* Benefits Section */}
      <Box display={"flex"} flexDirection={"column"} gap={3} sx={{ width: "100%" }}>
        <Box sx={{ width: "100%" }}>
          {shouldShowUpgradeInvitation && (
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: "none",
                backgroundColor: "var(--fx-surface-muted, #F1F5F9)",
                border: `1px solid ${alphaVar(colors.orange.base, 0.16)}`,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  borderBottom: `1px solid ${alphaVar(colors.gray[400], 0.1)}`,
                  p: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: colors.orange.base,
                  }}
                />

                <Typography
                  sx={{
                    color: colors.orange.base,
                    fontWeight: 800,
                    fontSize: "1rem",
                  }}
                >
                  Upgrade available
                </Typography>
              </Box>

              <Box
                sx={{
                  p: 1.5,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: { xs: "flex-start", sm: "center" },
                  gap: 2,
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    color: "var(--fx-surface-textsecondary, #64748B)",
                    maxWidth: 620,
                    lineHeight: 1.45,
                  }}
                >
                  You are currently using the free plan. Choose a paid plan to unlock more coverage areas, team seats, and premium business tools.
                </Typography>

                <Button
                  variant="contained"
                  endIcon={<East />}
                  onClick={handleUpgradeMySubscription}
                  sx={{
                    background: colors.orange.base,
                    color: "white",
                    fontWeight: 600,
                    textTransform: "none",
                    px: 3,
                    height: 35,
                    borderRadius: 2,
                    boxShadow: "none",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      bgcolor: colors.orange[600],
                      boxShadow: "none",
                    },
                  }}
                >
                  Choose a Plan
                </Button>
              </Box>
            </Card>
          )}

          {/* Cancel Subscription */}
          {subscription && !isFreeTrialPlan && subscription?.status !== ESubscriptionStatus.CANCELLED && (
            <Card
              sx={{
                borderRadius: 3,
                mt: 3,
                mb: { xs: 8, sm: 10 },
                boxShadow: "none",
                backgroundColor: "var(--fx-surface-base, #FFFFFF)",
                transition: "all 0.3s ease",
              }}
            >
              <Box
                sx={{
                  borderBottom: `1px solid ${alphaVar(colors.gray[400], 0.1)}`,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                  mb: 1.5,
                }}
              >
                <Box
                  sx={{
                    gap: 1,
                    p: 1.5,
                    pb: 1,
                    display: "flex",
                    color: colors.danger.base,
                    flexShrink: 0,
                    mt: 0.25,
                  }}
                >
                  <ReportGmailerrorred />
                  <Typography sx={{ color: colors.danger.base, fontWeight: 700, fontSize: "1rem" }}>Cancel Subscription</Typography>
                </Box>
              </Box>
              <Typography sx={{ px: 1.5, py: 1, fontWeight: 600, fontSize: "14px", color: "var(--fx-surface-textsecondary, #64748B)" }}>
                Your cancelled subscription will remain active until the end of the current billing period. You can reactivate it anytime.
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "flex-start", px: 1.5, pb: 2, mt: 1 }}>
                <Button
                  variant="outlined"
                  onClick={() => handleOpenDialog("cancel")}
                  sx={{
                    borderColor: colors.orange.base,
                    color: colors.orange.base,
                    fontWeight: 600,
                    height: 35,
                    px: 3,
                    fontSize: "12px",
                    borderRadius: 2,
                    "&:hover": {
                      borderColor: colors.orange.base,
                      bgcolor: colors.orange[50],
                    },
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Card>
          )}
        </Box>
      </Box>

      <LoaderComponent open={loading} type="spinner" />

      {/* Cancel Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        PaperProps={{ sx: { borderRadius: 3, backgroundColor: "var(--fx-surface-base, #FFFFFF)" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: colors.orange.base, pb: 1 }}>Cancel Subscription?</DialogTitle>
        <DialogContent sx={{ py: 2 }}>
          <Typography sx={{ color: colors.gray[400], fontSize: "0.95rem" }}>
            Once you cancel your subscription, you will lose access to premium benefits at the end of your current billing period.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderColor: colors.gray[400],
              color: colors.gray[500],
              "&:hover": {
                borderColor: colors.gray[500],
              },
            }}
          >
            Discard
          </Button>
          <Button
            onClick={handleAction}
            variant="contained"
            sx={{
              textTransform: "none",
              fontWeight: 700,
              bgcolor: colors.danger.base,
              color: "white",
              "&:hover": {
                bgcolor: colors.danger.base,
                opacity: 0.9,
              },
            }}
            disabled={loading}
          >
            {loading ? "Cancelling..." : "Confirm Cancellation"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
